import React, { useMemo, useState } from 'react';
import { Autocomplete, TextField, Checkbox, Box, CircularProgress, MenuItem, ListItemText } from '@mui/material';
import { Add } from '@mui/icons-material';
import { grey } from '@mui/material/colors';
import { useListAccountMasterv2Query } from '../api/accountMaster.api';
import { LedgerModal, useLedgerModalHandle } from '../index';

interface AccountOption {
  __typename?: string;
  accountname?: string | null;
  groupname?: string | null;
  accountsid?: number | null;
  accountgroupid?: number | null;
  tdssection?: string | null;
  tdspercentage?: number | null;
  accounttypeflag?: string | null;
  path?: string | null;
  is_active?: number | null;
}

interface AccountAutocompleteOption {
  label: string;
  value: number;
  groupName: string;
  AccountGroupID: number;
  TDSSection?: string | null;
  TDSPercentage?: number | null;
  AccountTypeFlag?: number | null;
  path?: string | null;
}

interface AccountAutocompleteProps {
  /** Currently selected account ID */
  value: number | null;
  /** Callback when account selection changes - receives structured account data */
  onChange: (accountData: {
    accountsId: number;
    groupId: number;
    isActive?: number | null;
    accountTypeCd?: string | null;
    partyId?: number | null;
  } | null) => void;
  /** Label for the input field */
  label?: string;
  
  /**
   * Exclude specific account group IDs from the dropdown
   * @example [5] - Hide all bank accounts (group ID 5)
   */
  excludeGroupIds?: number[];
  
  /**
   * Include ONLY specific account group IDs in the dropdown
   * When provided, this overrides excludeGroupIds and showAll behavior
   * @example [16] - Show only cash accounts (group ID 16)
   */
  includeOnlyGroupIds?: number[];
  
  /**
   * Show specific account group IDs at the top of the dropdown in the specified order
   * Lower array index = higher priority. Within each group, accounts are sorted alphabetically.
   * When showAll=true, priority groups appear first, followed by all other eligible accounts.
   * @example [16, 17] - Show cash accounts first (alphabetically), then bank accounts (alphabetically)
   */
  priorityGroupIds?: number[];
  
  /**
   * Exclude specific account IDs from the dropdown
   * @example [23] - Hide account with ID 23
   */
  excludeAccountIds?: number[];
  
  /**
   * Include ONLY specific account IDs in the dropdown
   * When provided, this overrides excludeAccountIds
   * @example [1, 2, 3] - Show only these specific accounts
   */
  includeAccountIds?: number[];
  
  /**
   * Hide inactive accounts (is_active !== 1) from the dropdown
   * @default true
   */
  hideInactive?: boolean;
  
  /**
   * Initially show all accounts regardless of excludeGroupIds
   * User can toggle with checkbox at bottom of dropdown
   * Auto-enabled when selected value is outside priority groups
   * @default false
   */
  showAllInitially?: boolean;
  
  /**
   * Callback when a new account is successfully created
   * Called after the new account is selected
   */
  onAddNew?: () => void;
  
  /** Size of the input field */
  size?: 'small' | 'medium';
  /** Disable the input field */
  disabled?: boolean;
  /** Show error state */
  error?: boolean;
  /** Helper text to display below the input */
  helperText?: string;
}

/**
 * AccountsAutocompleteComponent - A comprehensive account selection component with advanced filtering
 * 
 * Features:
 * - Priority group sorting with alphabetical ordering within groups
 * - Auto-enables "Show All" when selected value is outside priority groups
 * - Always excludes inactive accounts and explicitly excluded accounts/groups
 * - Smart filtering with include/exclude logic
 * 
 * @param props - Component props
 * @returns JSX.Element
 * 
 * @example
 * // Basic usage
 * <AccountsAutocomplete 
 *   value={selectedAccountId} 
 *   onChange={(accountData) => {
 *     setSelectedAccountId(accountData?.accountsId || null);
 *     console.log('Selected account:', accountData);
 *   }} 
 * />
 * 
 * @example
 * // Show only cash accounts with priority
 * <AccountsAutocomplete 
 *   value={selectedAccountId} 
 *   onChange={(accountData) => {
 *     setSelectedAccountId(accountData?.accountsId || null);
 *     // Access full account details
 *     console.log('Account ID:', accountData?.accountsId);
 *     console.log('Group ID:', accountData?.groupId);
 *     console.log('Is Active:', accountData?.isActive);
 *     console.log('Account Type:', accountData?.accountTypeCd);
 *   }}
 *   includeOnlyGroupIds={[16]}
 *   priorityGroupIds={[16, 17]}
 * />
 * 
 * @example
 * // Exclude specific accounts and groups
 * <AccountsAutocomplete 
 *   value={selectedAccountId} 
 *   onChange={(accountData) => setSelectedAccountId(accountData?.accountsId || null)}
 *   excludeGroupIds={[5, 6]}
 *   excludeAccountIds={[23, 24]}
 * />
 */
function AccountsAutocompleteComponent({
  value,
  onChange,
  label = 'Select Account',
  excludeGroupIds = [],
  includeOnlyGroupIds = [],
  priorityGroupIds = [],
  excludeAccountIds = [],
  includeAccountIds = [],
  hideInactive = true,
  showAllInitially = false,
  onAddNew,
  size = 'small',
  disabled = false,
  error = false,
  helperText,
}: AccountAutocompleteProps) {
  const [showAll, setShowAll] = useState(showAllInitially);
  const { openLedgerModal, closeLedgerModal, ledgerModalParams } = useLedgerModalHandle();

  /**
   * Handles the creation of a new account
   * Sets the new account ID as the selected value and calls the onAddNew callback
   * @param res - The response from the create mutation containing account data
   */
  const saveNewAccount = (res: {
    accountsId: number;
    groupId: number;
    isActive?: number | null;
    accountTypeCd?: string | null;
    partyId?: number | null;
  }) => {
    console.log('New account created:', res);
    // Pass the structured data to onChange
    onChange(res);
    // Call the onAddNew callback if provided
    onAddNew?.();
  };

  // ✅ Fetch all accounts
  const { data = [], isLoading } = useListAccountMasterv2Query(
    {},
    {
      refetchOnMountOrArgChange: true,
    }
  );

  // Auto-enable showAll if the selected value is outside priority groups
  const shouldShowAll = useMemo(() => {
    if (!value || priorityGroupIds.length === 0) return showAllInitially;
    
    // Check if the selected value exists in the priority groups
    const selectedAccount = data?.find(account => account?.accountsid === value);
    if (!selectedAccount) return showAllInitially;
    
    const isInPriorityGroup = priorityGroupIds.includes(selectedAccount.accountgroupid || 0);
    
    // If selected account is not in priority groups, auto-enable showAll
    return showAllInitially || !isInPriorityGroup;
  }, [value, priorityGroupIds, data, showAllInitially]);

  // Update showAll state when shouldShowAll changes
  React.useEffect(() => {
    setShowAll(shouldShowAll);
  }, [shouldShowAll]);

  /**
   * Transforms and filters account data based on component props
   * Applies filtering in the following order:
   * 1. Basic null/undefined filtering
   * 2. Hide inactive accounts (if hideInactive is true)
   * 3. Account-level filtering (includeAccountIds overrides excludeAccountIds)
   * 4. Group-level filtering (includeOnlyGroupIds overrides excludeGroupIds)
   * 5. Priority sorting based on priorityGroupIds
   * 
   * Sorting Logic:
   * - If priorityGroupIds provided and showAll=false: Show ONLY priority groups in order, accounts alphabetically within each
   * - If priorityGroupIds provided and showAll=true: Priority groups first, then all other eligible accounts alphabetically
   * - If no priority: All accounts sorted alphabetically by group name, then account name
   * 
   * @returns Filtered and sorted account options
   */
  const options = useMemo(() => {
    if (!data) return [];

    // Step 1: Basic null/undefined filtering
    let filtered = data.filter(
      (item) => item !== null && item.accountname !== null && item.accountsid !== null && item.accountgroupid !== null
    );

    // Step 2: Hide inactive accounts if hideInactive is true
    if (hideInactive) {
      filtered = filtered.filter((item) => item?.is_active === 1);
    }

    // Step 3: Account-level filtering (includeAccountIds takes precedence over excludeAccountIds)
    if (includeAccountIds.length > 0) {
      // Show ONLY these specific account IDs
      filtered = filtered.filter((item) => item?.accountsid && includeAccountIds.includes(item?.accountsid));
    } else if (excludeAccountIds.length > 0) {
      // Hide these specific account IDs
      filtered = filtered.filter((item) => item?.accountsid && !excludeAccountIds.includes(item?.accountsid));
    }

    // Step 4: Group-level filtering (includeOnlyGroupIds takes precedence over excludeGroupIds)
    if (includeOnlyGroupIds.length > 0) {
      // Show ONLY accounts from these specific group IDs
      filtered = filtered.filter((item) => item?.accountgroupid && includeOnlyGroupIds.includes(item?.accountgroupid));
    } else if (!showAll && excludeGroupIds.length > 0) {
      // Hide accounts from these specific group IDs (unless showAll is true)
      filtered = filtered.filter((item) => item?.accountgroupid && !excludeGroupIds.includes(item?.accountgroupid));
    }

    // Step 4.5: When showAll is false and priorityGroupIds is provided, show ONLY priority groups
    if (!showAll && priorityGroupIds.length > 0) {
      filtered = filtered.filter((item) => item?.accountgroupid && priorityGroupIds.includes(item?.accountgroupid));
    }

    // Step 5: Priority sorting logic
    if (priorityGroupIds.length > 0) {
      // Create a map of group IDs to their priority order (lower index = higher priority)
      const priorityMap = new Map<number, number>();
      priorityGroupIds.forEach((id, index) => priorityMap.set(id, index));

      filtered.sort((a, b) => {
        // Get priority for each account's group (MAX_SAFE_INTEGER if not in priority list)
        const aPriority =
          a?.accountgroupid && priorityMap.has(a?.accountgroupid) ? priorityMap.get(a?.accountgroupid)! : Number.MAX_SAFE_INTEGER;
        const bPriority =
          b?.accountgroupid && priorityMap.has(b?.accountgroupid) ? priorityMap.get(b?.accountgroupid)! : Number.MAX_SAFE_INTEGER;

        // First sort by priority (lower number = higher priority)
        if (aPriority !== bPriority) return aPriority - bPriority;
        
        // If same priority group, sort alphabetically by account name
        if (a?.accountgroupid === b?.accountgroupid) {
          return (a?.accountname || '').localeCompare(b?.accountname || '');
        }
        
        // If different groups but same priority (both non-priority), sort alphabetically by group name then account name
        const groupComparison = (a?.accountgroupname || '').localeCompare(b?.accountgroupname || '');
        if (groupComparison !== 0) return groupComparison;
        return (a?.accountname || '').localeCompare(b?.accountname || '');
      });
    } else {
      // No priority groups specified, sort alphabetically by group name then account name
      filtered.sort((a, b) => {
        const groupComparison = (a?.accountgroupname || '').localeCompare(b?.accountgroupname || '');
        if (groupComparison !== 0) return groupComparison;
        return (a?.accountname || '').localeCompare(b?.accountname || '');
      });
    }

    return filtered.map(
      (item): AccountAutocompleteOption => ({
        label: item?.accountname || '',
        value: item?.accountsid || 0,
        groupName: item?.accountgroupname || '',
        AccountGroupID: item?.accountgroupid || 0,
        TDSSection: item?.tdssection,
        TDSPercentage: item?.tdspercentage,
        AccountTypeFlag: item?.accounttypeflag || 0,
        path: item?.path,
      })
    );
  }, [data, hideInactive, excludeAccountIds, includeAccountIds, excludeGroupIds, includeOnlyGroupIds, showAll, priorityGroupIds]);

  const addNewOption = useMemo(
    () => [
      {
        label: '➕ Add New Account',
        value: -1,
        groupName: '',
        AccountGroupID: -1,
        TDSSection: null,
        TDSPercentage: null,
        AccountTypeFlag: null,
        path: null,
        raw: {} as AccountOption,
      } as AccountAutocompleteOption,
    ],
    []
  );

  const showAllOption = useMemo(
    () => [
      {
        label: 'Show All Accounts',
        value: -2,
        groupName: '',
        AccountGroupID: -2,
        TDSSection: null,
        TDSPercentage: null,
        AccountTypeFlag: null,
        path: null,
        raw: {} as AccountOption,
      } as AccountAutocompleteOption,
    ],
    []
  );

  return (
    <>
      <Autocomplete
        size={size}
        disabled={disabled}
        loading={isLoading}
        options={[...addNewOption, ...options, ...showAllOption]}
        value={value ? options.find((opt) => opt.value === value) || null : null}
        getOptionLabel={(opt) => opt?.label || ''}
        isOptionEqualToValue={(option, value) => option.value === value.value}
        onChange={(_, newValue) => {
          if (newValue?.value === -1) {
            openLedgerModal({ groupId: includeOnlyGroupIds?.[0], cx: {} });
            return;
          }
          if (newValue?.value === -2) {
            setShowAll(!showAll);
            return;
          }
          if (newValue) {
            // Find the account in the data to get full details
            const selectedAccount = data?.find(account => account?.accountsid === newValue.value);
            if (selectedAccount) {
              onChange({
                accountsId: selectedAccount.accountsid || 0,
                groupId: selectedAccount.accountgroupid || 0,
                isActive: selectedAccount.is_active || null,
                accountTypeCd: selectedAccount.accounttypeflag ? String(selectedAccount.accounttypeflag) : null,
                partyId: null, // This might need to be determined based on your business logic
              });
            } else {
              // If account not found in data, pass minimal data
              onChange({
                accountsId: newValue.value,
                groupId: 0,
                isActive: null,
                accountTypeCd: null,
                partyId: null,
              });
            }
          } else {
            onChange(null);
          }
        }}
        renderOption={(props, option) => {
          if (option.value === -1) {
            return (
              <MenuItem
                value={'addAccount'}
                onClick={(e) => {
                  e.stopPropagation();
                  openLedgerModal({ groupId: includeOnlyGroupIds?.[0], cx: {} });
                }}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  position: 'sticky',
                  top: '0px !important',
                  backgroundColor: grey[50],
                  '&:hover': {
                    backgroundColor: grey[200],
                  },
                }}
              >
                <Add sx={{ marginRight: '5px', color: 'primary.main' }} />
                <ListItemText sx={{ color: 'primary.main' }} primary="Add New Account" />
              </MenuItem>
            );
          }

          if (option.value === -2) {
            return (
              <MenuItem
                value={'showAllAccounts'}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  position: 'sticky',
                  bottom: '-8px',
                  mb: '-8px',
                  zIndex: 1,
                  ml: '-10px',
                  backgroundColor: grey[50],
                  '&:hover': {
                    backgroundColor: grey[200],
                  },
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  setShowAll(!showAll);
                }}
              >
                <Checkbox
                  checked={showAll}
                  sx={{
                    marginRight: '5px',
                    color: 'primary.main',
                  }}
                  onChange={(e) => {
                    e.stopPropagation();
                    setShowAll(e.target.checked);
                  }}
                />
                <ListItemText sx={{ color: 'primary.main' }} primary="Show all accounts" />
              </MenuItem>
            );
          }

          return (
            <li {...props}>
              <span>
                <b>{option.label}</b> {option?.groupName?.length ? <i>({option?.groupName?.trim()})</i> : ''}
              </span>
            </li>
          );
        }}
        ListboxProps={{
          style: { paddingTop: 0 },
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            placeholder="Search Account"
            error={error}
            helperText={helperText}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {isLoading ? <CircularProgress color="inherit" size={20} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        componentsProps={{
          popper: {
            modifiers: [
              {
                name: 'flip',
                enabled: false,
              },
            ],
          },
        }}
      />

      <LedgerModal
        {...ledgerModalParams}
        onSave={(res) => saveNewAccount(res)}
        onClose={() => {
          closeLedgerModal();
        }}
      />
    </>
  );
}

const AccountsAutocomplete = React.memo(AccountsAutocompleteComponent);
export default AccountsAutocomplete;
